import React, { PureComponent } from "react";
import { Label, Grid, Input, Form, Divider, Button, Dropdown } from "semantic-ui-react";
import { get, isEmpty } from "lodash";

import { Link } from "react-router-dom";
import AppLink from "../Common/AppLink";
import Layout from "../Common/Layout";
import AppButton from "../Common/AppButton";
import Loader from "../Common/Loader";
import ProductBundles from "../Products/FormArray/ProductBundles";
import ProductLicenseBundles from "../Products/FormArray/ProductLicenseBundles";
import ProductFeatures from "../Products/FormArray/ProductFeatures";

export const EditTemplate = ({
  template,
  errors,
  updateField,
  touched,
  templatesBundlesOptions,
  templatesLicenseBundlesOptions
}) => {
  return (
    <Grid.Row>
      <Grid.Column>
        <Form widths="equal">
          <Grid padded>
            {!isEmpty(template.products) && (
              <Grid.Row columns={1}>
                <Grid.Column>
                  <Divider horizontal>PRODUCTS</Divider>
                </Grid.Column>
              </Grid.Row>
            )}
            {!isEmpty(template.products) && (
              <Grid.Row>
                <Grid.Column textAlign="center">
                  {template.products.map((product, index) => {
                    return <AppLink key={index} to={`/products/${product.slug}/edit`} size="large" content={product.name} />;
                  })}
                </Grid.Column>
              </Grid.Row>
            )}
            {!isEmpty(template.products) && (
              <Grid.Row columns={1}>
                <Grid.Column>
                  <Divider />
                </Grid.Column>
              </Grid.Row>
            )}
            <Grid.Row>
              <Grid.Column verticalAlign="middle" width={5}>
                <label>Name</label>
              </Grid.Column>
              <Grid.Column width={7} stretched textAlign="center">
                <Input
                  fluid
                  name="name"
                  required
                  error={touched && isEmpty(template.name)}
                  placeholder="Name"
                  value={template.name}
                  onChange={(e, data) => updateField({ name: data.value }, true)}
                />
                {isEmpty(template.name) && touched && (
                  <Label basic color="red" pointing>
                    Name is required
                  </Label>
                )}
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column verticalAlign="middle" width={5}>
                <label>Tiered bundle</label>
              </Grid.Column>
              <Grid.Column width={7} stretched textAlign="center">
                <Dropdown
                  disabled={isEmpty(templatesBundlesOptions)}
                  name="tieredBundle"
                  placeholder="Tiered bundle"
                  fluid
                  error={isEmpty(templatesBundlesOptions) || isEmpty(template.tieredBundle)}
                  onBlur={(e, data) => updateField({ tieredBundle: data.value })}
                  onChange={(e, data) => updateField({ tieredBundle: data.value })}
                  value={template.tieredBundle}
                  selection
                  options={templatesBundlesOptions}
                />
                {isEmpty(template.tieredBundle) && touched && (
                  <Label basic color="red" pointing>
                    Tiered bundle is required
                  </Label>
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Grid.Column>
    </Grid.Row>
  );
};

class TemplateForm extends PureComponent {
  state = { errors: null, templatesFeatures: [], templatesBundles: [], templatesLicenseBundles: [] };

  static getDerivedStateFromProps(nextProps, state) {
    const { template, bundles, licenseBundles, features } = nextProps;

    const nextState = {};

    if (bundles && !state.bundleOptions) {
      nextState.bundleOptions = bundles
        .filter(bundle => !bundle.archived)
        .map(bundle => ({
          key: bundle.slug,
          text: bundle.name + ` (${bundle.slug})`,
          value: bundle.slug
        }));
      nextState.bundles = [...bundles];
    }

    if (licenseBundles && !state.licenseBundleOptions) {
      nextState.licenseBundleOptions = licenseBundles
        .filter(bundle => !bundle.archived)
        .map(bundle => ({
          key: bundle.slug,
          text: bundle.name + ` (${bundle.slug})`,
          value: bundle.slug
        }));
      nextState.licenseBundles = [...licenseBundles];
    }

    if (features && !state.featureOptions) {
      nextState.featureOptions = features
        .filter(feature => !feature.archived)
        .map(feature => ({
          text: feature.name + ` (${feature.slug})`,
          value: feature.slug
        }));
      nextState.features = [...features];
    }

    if (template && !state.template) {
      nextState.template = template;
      if (template.templatesFeatures && isEmpty(state.templatesFeatures)) {
        nextState.templatesFeatures = template.templatesFeatures.map(item => ({
          ...item,
          featureSlug: item.feature.slug
        }));
      }

      if (template.templatesBundles && isEmpty(state.templatesBundles)) {
        nextState.templatesBundles = template.templatesBundles.map(item => ({
          ...item,
          bundleSlug: item.bundle.slug
        }));

        const tiered = template.templatesBundles.find(tBundle => tBundle.tiered === true);
        if (tiered) {
          nextState.template.tieredBundle = tiered.bundle.id;
        }
      }

      if (template.templatesLicenseBundles && isEmpty(state.templatesLicenseBundles)) {
        nextState.templatesLicenseBundles = template.templatesLicenseBundles.map(item => ({
          ...item,
          bundleSlug: item.licenseBundle.slug
        }));

        const tiered = template.templatesLicenseBundles.find(tBundle => tBundle.tiered === true);
        if (tiered) {
          nextState.template.tieredLicenseBundle = tiered.licenseBundle.id;
        }
      }
    }

    return { ...state, ...nextState };
  }

  onUpdateField = (data, touched) => {
    const { template } = this.state;
    this.setState({ template: { ...template, ...data }, touched: touched });
  };

  handleAddTemplatesBundle = fields => this.handleAddBundle(fields);
  handleRemoveTemplatesBundle = idx => this.handleRemoveBundleOrFeature("templatesBundles", idx);
  handleUpdateTemplatesBundle = (type, id, data) => this.handleUpdateBundleOrFeature("templatesBundles", id, data);

  handleAddTemplatesLicenseBundle = fields => this.handleAddLicenseBundle(fields);
  handleRemoveTemplatesLicenseBundle = idx => this.handleRemoveBundleOrFeature("templatesLicenseBundles", idx);
  handleUpdateTemplatesLicenseBundle = (type, id, data) =>
    this.handleUpdateBundleOrFeature("templatesLicenseBundles", id, data);

  handleAddTemplatesFeature = fields => this.handleAddFeature(fields);
  handleRemoveTemplatesFeature = idx => this.handleRemoveBundleOrFeature("templatesFeatures", idx);
  handleUpdateTemplatesFeature = (type, id, data) => this.handleUpdateBundleOrFeature("templatesFeatures", id, data);

  handleUpdateBundleOrFeature = (type, id, data) => {
    this.setState(prevState => {
      let index = this.state[type].findIndex(el => el.id === id);

      let items = [...prevState[type]];
      items[index].visible = data.checked;
      return { [type]: items };
    });
  };

  handleAddBundle = data => {
    const { templatesBundles } = this.state;
    let slugs = templatesBundles.map(item => item.bundleSlug);

    if (slugs.includes(data.bundleSlug)) {
      let idx = slugs.indexOf(data.bundleSlug);
      this.setState(prevState => {
        const updatedItems = [...prevState.templatesBundles];
        updatedItems[idx].removed = false;
        updatedItems[idx].visible = data.visible;
        updatedItems[idx].size = data.size;
        updatedItems[idx].term = data.term;
        return { templatesBundles: updatedItems };
      });
    } else {
      this.setState(prevState => {
        return { templatesBundles: [...prevState.templatesBundles, { ...data }] };
      });
    }
  };

  handleAddLicenseBundle = data => {
    const { templatesLicenseBundles } = this.state;
    let slugs = templatesLicenseBundles.map(item => item.bundleSlug);

    if (slugs.includes(data.bundleSlug)) {
      let idx = slugs.indexOf(data.bundleSlug);
      this.setState(prevState => {
        const updatedItems = [...prevState.templatesLicenseBundles];
        updatedItems[idx].removed = false;
        updatedItems[idx].visible = data.visible;
        updatedItems[idx].size = data.size;
        updatedItems[idx].term = data.term;
        return { templatesLicenseBundles: updatedItems };
      });
    } else {
      this.setState(prevState => {
        return { templatesLicenseBundles: [...prevState.templatesLicenseBundles, { ...data }] };
      });
    }
  };

  handleAddFeature = data => {
    const { templatesFeatures } = this.state;
    let slugs = templatesFeatures.map(item => item.featureSlug);
    if (data.value instanceof Array) {
      data.value = data.value.join(" ");
    }
    if (slugs.includes(data.featureSlug)) {
      let idx = slugs.indexOf(data.featureSlug);
      this.setState(prevState => {
        const updatedItems = [...prevState.templatesFeatures];
        updatedItems[idx].removed = false;
        updatedItems[idx].value = data.value;
        updatedItems[idx].visible = data.visible;
        return { templatesFeatures: updatedItems };
      });
    } else {
      this.setState(prevState => {
        return { templatesFeatures: [...prevState.templatesFeatures, { ...data }] };
      });
    }
  };

  handleRemoveBundleOrFeature = (type, idx) => () => {
    this.setState(prevState => {
      let items = [...prevState[type]];
      items[idx].removed = true;
      return { [type]: items };
    }, this.removeTypeSpecificOps(type, idx));
  };

  removeTypeSpecificOps(type, idx) {
    if (!["templatesBundles", "templatesLicenseBundles"].includes(type)) return;

    this.setState(prevState => {
      let items = [...prevState[type]];
      const { bundleSlug } = items[idx];
      const { template } = prevState;
      if (template.tieredBundle !== bundleSlug) return prevState;

      return { template: { ...prevState.template, tieredBundle: null } };
    });
  }
  selectableFeatures = (featureOptions, templatesFeatures) => {
    const templatesFeaturesIds = templatesFeatures
      .filter(templatesFeature => templatesFeature.removed !== true)
      .map(templatesFeature => templatesFeature.featureSlug);

    return featureOptions.filter(featureOption => !templatesFeaturesIds.includes(featureOption.value));
  };

  selectableBundles = (bundleOptions, templatesBundles) => {
    const templatesBundlesIds = templatesBundles
      .filter(templatesBundle => templatesBundle.removed !== true)
      .map(templateBundle => templateBundle.bundleSlug);

    return bundleOptions.filter(bundleOption => !templatesBundlesIds.includes(bundleOption.value));
  };

  selectableLicenseBundles = (licenseBundleOptions, templatesLicenseBundles) => {
    const templatesLicenseBundlesIds = templatesLicenseBundles
      .filter(templatesLicenseBundle => templatesLicenseBundle.removed !== true)
      .map(templateLicenseBundle => templateLicenseBundle.bundleSlug);

    return licenseBundleOptions.filter(
      licenseBundleOption => !templatesLicenseBundlesIds.includes(licenseBundleOption.value)
    );
  };

  save = () => {
    const {
      template: stateTemplate,
      templatesFeatures,
      templatesBundles: stateTemplatesBundles,
      templatesLicenseBundles: stateTemplatesLicenseBundles
    } = this.state;
    const { tieredBundle, ...template } = stateTemplate;
    const templatesBundles = stateTemplatesBundles.map(tBundle => ({
      ...tBundle,
      tiered: tBundle.bundle ? tBundle.bundle.id === tieredBundle : tBundle.bundleSlug === tieredBundle
    }));
    const templatesLicenseBundles = stateTemplatesLicenseBundles.map(tBundle => ({
      ...tBundle,
      tiered: false
    }));
    this.props.saveTemplateChanges({ template, templatesFeatures, templatesBundles, templatesLicenseBundles });
  };

  render() {
    const {
      errors,
      template,
      bundleOptions,
      licenseBundleOptions,
      featureOptions,
      templatesFeatures,
      templatesBundles,
      templatesLicenseBundles,
      touched
    } = this.state;
    const {
      bundles,
      licenseBundles,
      features,
      isRenderFeatures,
      isRenderBundles,
      isRenderLicenseBundles,
      page
    } = this.props;

    const templatesBundlesOptions = bundleOptions
      ? templatesBundles
          .filter(tBundle => tBundle.removed !== true)
          .map(tBundle => ({
            key: tBundle.bundleSlug,
            text: `${bundleOptions.find(o => o.key === tBundle.bundleSlug).text} (${tBundle.size} ${tBundle.term})`,
            value: get(tBundle, "bundle.id", tBundle.bundleSlug)
          }))
      : [];

    const templatesLicenseBundlesOptions = licenseBundleOptions
      ? templatesLicenseBundles
          .filter(tBundle => tBundle.removed !== true)
          .map(tBundle => ({
            key: tBundle.bundleSlug,
            text: `${licenseBundleOptions.find(o => o.key === tBundle.bundleSlug).text} (${tBundle.size} ${tBundle.term})`,
            value: get(tBundle, "licenseBundle.id", tBundle.bundleSlug)
          }))
      : [];

    return (
      <>
        <Layout.Page>
          <Layout.Header
            link={<AppButton icon="angle left" content="Back" as={Link} to="/templates" size="large" />}
            title={`${page} template`}
          />
          {!isEmpty(template) ? (
            <Layout.Content>
              <Grid relaxed padded stackable>
                <EditTemplate
                  touched={touched}
                  template={template}
                  errors={errors}
                  updateField={this.onUpdateField}
                  templatesBundlesOptions={templatesBundlesOptions}
                  templatesLicenseBundlesOptions={templatesLicenseBundlesOptions}
                />
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Divider />
                  </Grid.Column>
                </Grid.Row>
                {isRenderFeatures() && (
                  <Grid.Row>
                    <Grid.Column width={14}>
                      <ProductFeatures
                        featureOptions={this.selectableFeatures(featureOptions, templatesFeatures)}
                        productMetadataItems={templatesFeatures}
                        features={features}
                        onItemAdd={this.handleAddTemplatesFeature}
                        onItemRemove={this.handleRemoveTemplatesFeature}
                        onItemUpdate={this.handleUpdateTemplatesFeature}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Divider />
                    <br />
                  </Grid.Column>
                </Grid.Row>
                {isRenderBundles() && (
                  <Grid.Row>
                    <Grid.Column width={14}>
                      <ProductBundles
                        productBundles={templatesBundles}
                        bundleOptions={this.selectableBundles(bundleOptions, templatesBundles)}
                        bundles={bundles}
                        onItemAdd={this.handleAddTemplatesBundle}
                        onItemRemove={this.handleRemoveTemplatesBundle}
                        onItemUpdate={this.handleUpdateTemplatesBundle}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                {isRenderLicenseBundles() && (
                  <Grid.Row>
                    <Grid.Column width={14}>
                      <ProductLicenseBundles
                        productLicenseBundles={templatesLicenseBundles}
                        licenseBundleOptions={this.selectableLicenseBundles(licenseBundleOptions, templatesLicenseBundles)}
                        licenseBundles={licenseBundles}
                        onItemAdd={this.handleAddTemplatesLicenseBundle}
                        onItemRemove={this.handleRemoveTemplatesLicenseBundle}
                        onItemUpdate={this.handleUpdateTemplatesLicenseBundle}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                <Grid.Row>
                  <Grid.Column>
                    <Button
                      data-test="updateTemplateBtn"
                      color="orange"
                      disabled={isEmpty(template.name) || isEmpty(template.tieredBundle)}
                      onClick={this.save}
                      content={page === "Create" ? page : "Update"}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Layout.Content>
          ) : (
            <Loader message="Loading template..." />
          )}
        </Layout.Page>
      </>
    );
  }
}

export default TemplateForm;
